import * as React from "react";
import { FileManagementFieldSchema, IInputFieldProps } from "@cak-legacy/generation-core";

import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  dropTargetForExternal,
  monitorForExternal
} from "@atlaskit/pragmatic-drag-and-drop/external/adapter";
import { containsFiles, getFiles } from "@atlaskit/pragmatic-drag-and-drop/external/file";
import { preventUnhandled } from "@atlaskit/pragmatic-drag-and-drop/prevent-unhandled";

import { Button, InputFieldProps } from "@cak-legacy/shared-common";
import { withRegisterFieldHoc } from "../Hoc/withRegisterFieldHoc";
import { withRenderFieldHoc } from "../Hoc/withRenderFieldHoc";
import { useFormContext, useWatch } from "react-hook-form";
import "./FileField.scss";
import { FileManagementFile } from "../Interfaces/fileManagementFile";
import "./FileManagementField.scss";
import { convertCallbackToValidationArray } from "../Utils/validate";
import { createFileManagementFieldModel } from "./utils/createFileManagementFieldModel";
import { useUploads } from "./hooks/useUploads";
import { FileList } from "../Components/File/FileList";
import { withFormFieldHoc } from "./hoc/withFormFieldHoc";

const RegisteredFileField = withRegisterFieldHoc(
  withRenderFieldHoc((props: IInputFieldProps<FileManagementFieldSchema> & InputFieldProps) => {
    const ref = React.useRef<HTMLDivElement | null>(null);
    const [state, setState] = React.useState<"idle" | "potential" | "over">("idle");

    const { setValue } = useFormContext();
    const currentValue = useWatch({ name: props.name });
    const { addUploadedFile } = useUploads(props.name);

    const addUpload = React.useCallback((file: File | null) => {
      if (!file) {
        return;
      }
      const upload: FileManagementFile = {
        file,
        fileName: file.name,
        deleted: false,
        id: 0,
        referenceId: crypto.randomUUID()
      };
      addUploadedFile(upload);
    }, []);

    const onFileInputChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.currentTarget.files ?? []);
        files.forEach(addUpload);
      },
      [addUpload]
    );

    React.useEffect(() => {
      const el = ref.current;
      if (!el) {
        throw new Error(`undefined element found`);
      }
      return combine(
        dropTargetForExternal({
          element: el,
          canDrop: containsFiles,
          onDragEnter: () => setState("over"),
          onDragLeave: () => setState("potential"),
          onDrop: async ({ source }) => {
            const files = await getFiles({ source });

            files.forEach((file) => {
              if (file == null) {
                return;
              }
              const upload: FileManagementFile = {
                file,
                fileName: file.name,
                deleted: false,
                id: 0,
                referenceId: crypto.randomUUID()
              };
              addUploadedFile(upload);
            });
          }
        }),
        monitorForExternal({
          canMonitor: containsFiles,
          onDragStart: () => {
            setState("potential");
            preventUnhandled.start();
          },
          onDrop: () => {
            setState("idle");
            preventUnhandled.stop();
          }
        })
      );
    });

    const inputRef = React.useRef<HTMLInputElement>(null);
    const onInputTriggerClick = React.useCallback(() => {
      inputRef.current?.click();
    }, []);

    return (
      <div className="file-management">
        <div
          ref={ref}
          data-testid="file-drop-target"
          className={`file-management__drop-target ${
            state === "over" ? "over" : state === "potential" ? "potential" : undefined
          }`}
        >
          <strong>{props.field.dropFilesText}</strong>

          <Button clickHandler={onInputTriggerClick} buttonText={props.field.buttonText} />

          <input
            ref={inputRef}
            id="file-input"
            onChange={onFileInputChange}
            type="file"
            accept={props.field.acceptFileTypes}
            multiple
          />
        </div>
        <FileList
          files={currentValue?.files ?? []}
          validate={convertCallbackToValidationArray(props.field.validate)}
          onDelete={(file) => {
            file.deleted = true;
            setValue(
              props.name,
              createFileManagementFieldModel(
                [],
                (currentValue?.files ?? []).filter((file) => (!file.deleted && !file.id) || file.id)
              ),
              { shouldValidate: true }
            );
          }}
        />
      </div>
    );
  })
);

export const FileManagementField = React.memo(
  withFormFieldHoc((props: IInputFieldProps<FileManagementFieldSchema>) => (
    <RegisteredFileField {...props} displayStyle="block" />
  ))
);
